import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { GetStaticPaths, GetStaticProps } from "next";
import { getNetworkURL, Routes } from "~/routes";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import {
    AggregateQueryMode,
    Filter,
    FilterMetadata,
    FilterOperators,
    QueryResponse,
    SortDirections,
} from "~/modules/filters/types";
import { Footer } from "~/components/Footer";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { isNullOrUndefined } from "~/utils/isNullOrUndefined";
import {
    NEXTJS_MISS_REVALIDATE_TIMEOUT,
    NEXTJS_REVALIDATE_TIMEOUT,
} from "~/server/consts";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { queryNetworks } from "~/modules/networks/api/router";
import {
    Network,
    NetworkColumns,
    ParentNetworkMetadata,
} from "~/modules/networks/types";
import {
    getNetworkPageDescription,
    NetworkIndividualWebpageSEO,
} from "~/modules/networks/seo/NetworkIndividualWebpageSEO";
import { NetworkIndividualPage } from "~/modules/networks/pages/individual";
import { queryTokenFilterMeta } from "~/modules/tokens/api/router";
import { queryServiceProviders } from "~/modules/service_providers/api/router";
import { DEFAULT_SERVICE_PROVIDER_LIST_QUERY } from "~/modules/service_providers/consts";
import {
    ServiceProvider,
    ServiceProviderColumns,
} from "~/modules/service_providers";
import { Networks } from "~/schema";
import { TokenColumns } from "~/modules/tokens/types";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import {
    ASSET_CLASS_IDS,
    DISTRIBUTION_TYPE_TOOLTIPS,
    MEASURE_IDS,
    SOCIAL_OG_IMAGE_URL,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import { queryTokenAggregateValueWith30D } from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import { TokenTimeseriesColumns } from "~/modules/token_timeseries/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { AggregateValueTypes } from "~/components/AggregateDisplay/types";
import {
    SIXTY_DAYS_AGO,
    THIRTY_DAYS_AGO,
} from "~/modules/visualization/components/TimeSeriesChartV3/consts";
import { queryTokenAggregateLeagueTable } from "~/modules/league_tables/api/queryTokenAggregateLeagueTable";

// // // //

// Helper function to get the RWA asset count for a network (all non-stablecoin assets)
function getNetworkRwaAssetCount(network: Network): number {
    const stablecoin_stats = network.asset_class_stats.find(
        (ac) => ac.id === ASSET_CLASS_IDS.Stablecoin,
    );

    const asset_count = network.asset_count || 0;
    const rwa_asset_count = asset_count - (stablecoin_stats?.asset_count || 0);
    return rwa_asset_count;
}

const HEDERA_NETWORK_SLUG = "hedera-hashgraph";

export const getStaticPaths: GetStaticPaths = async () => {
    const networkListRes = await queryNetworks({
        query: {
            sort: {
                field: NetworkColumns.name,
                direction: SortDirections.asc,
            },
            pagination: {
                page: 1,
                perPage: 250,
            },
        },
    });

    // Redirect Hedera network individual page to Network Listing Page
    // NOTE - this must be removed when we're ready to release the Hedera network page
    // const networks: Network[] = networkListRes.results;
    const networks: Network[] = networkListRes.results.filter(
        (n) => n.slug !== HEDERA_NETWORK_SLUG,
    );

    // Collect all slugs
    const allSlugs: Array<string> = networks
        .filter((n) => typeof n.slug === "string")
        .map((n) => n.slug) as Array<string>;

    // Ensure uniqueness just to be safe
    const uniqueSlugs = uniqueValues(allSlugs);

    // Create paths for all networks returned by the above query
    const paths = uniqueSlugs.map((each) => {
        return {
            params: {
                networkSlug: each,
            },
        };
    });

    return {
        paths,
        fallback: "blocking", // false or "blocking"
    };
};

// // // //

// Helper function to fetch an Network by Network.slug
async function getNetworkBySlug(networkSlug: string): Promise<Network | null> {
    // Query for EXACT match of Network.slug (includes upper + lowercase)
    const networkResBySlugStrict = await queryNetworks({
        query: {
            sort: {
                field: NetworkColumns.name,
                direction: SortDirections.asc,
            },
            pagination: {
                page: 1,
                perPage: 250,
            },
        },
    });

    // Define local network variable as null
    let network: Network | undefined | null = null;

    // Lookup networl by case-insensitive `ticker` property
    network = networkResBySlugStrict.results.find(
        (each) => each.slug?.toLowerCase() === networkSlug.toLowerCase(),
    );

    // Return Network | null
    return isNullOrUndefined(network) ? null : network;
}

// // // //

// Helper function to fetch an asset by Asset.ticker OR Asset.id
async function getNetwork(networkSlug: string): Promise<Network | null> {
    // Define local network variable as null
    let network: Network | undefined | null = null;

    // Check for strict equality by ticker
    network = await getNetworkBySlug(networkSlug);

    // Return Network | null
    return isNullOrUndefined(network) ? null : network;
}

// // // //

export const getStaticProps: GetStaticProps<{
    network: Network;
}> = async (ctx) => {
    const networkSlug = ctx.params?.networkSlug as string;

    if (!networkSlug) {
        console.error("Network slug is missing from params");
        return {
            notFound: true,
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    // Query the network
    const network: Network | null = await getNetwork(networkSlug);

    // Handle network not found
    if (network === null) {
        console.warn(
            `Network not found for slug "${networkSlug}". Redirecting to home.`,
        );
        return {
            redirect: {
                permanent: false,
                destination: Routes.home,
            },
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    // Query child networks
    const childNetworks = await queryNetworks({
        query: {
            filter: {
                field: NetworkColumns.parent_network_id,
                operator: FilterOperators.Equals,
                value: network.id,
            },
            sort: {
                direction: SortDirections.asc,
                field: NetworkColumns.name,
            },
            pagination: {
                page: 1,
                perPage: 500,
            },
        },
    });

    // Define flag to check if the network has child networks
    const hasChildNetworks = childNetworks.results.length > 0;

    // Construct query for token filter metadata
    const tokenFilterQuery: Filter = hasChildNetworks
        ? {
              operator: FilterOperators.Or,
              filters: childNetworks.results.map((childNetwork) => ({
                  field: TokenColumns.network_slug,
                  operator: FilterOperators.Equals,
                  value: childNetwork.slug,
              })),
          }
        : {
              field: TokenColumns.network_slug,
              operator: FilterOperators.Equals,
              value: network.slug,
          };

    // Query token filter metadata
    const tokenFilterMetadata = await queryTokenFilterMeta({
        filter: tokenFilterQuery,
    });

    // Empty QueryResponse for ServiceProviders
    let serviceProviders: QueryResponse<ServiceProvider> = {
        results: [],
        sort: {
            direction: SortDirections.asc,
            field: ServiceProviderColumns.name,
        },
        pagination: {
            page: 1,
            perPage: 25,
            resultCount: 4,
            pageCount: 1,
        },
    };

    // Fetch ServiceProviders for Aptos + ZkSync ONLY
    if (
        [Networks.aptos, Networks.zksyncEra].includes(network.slug as Networks)
    ) {
        try {
            serviceProviders = await queryServiceProviders({
                query: {
                    ...DEFAULT_SERVICE_PROVIDER_LIST_QUERY,
                    filter: {
                        field: ServiceProviderColumns.network_ids,
                        operator: FilterOperators.Includes,
                        value: network.id,
                    },
                },
            });
        } catch (error) {
            console.error("Error fetching service providers:", error);
            // Continue with empty service providers rather than failing
        }
    }

    // Log successful network fetch for monitoring
    console.info(
        `Successfully generated props for network "${network.slug}" (${network.id})`,
    );

    // Fetch parent network if exists
    let parentNetwork: Network | null = null;
    if (network.parent_network_id) {
        try {
            const parentNetworkRes = await queryNetworks({
                query: {
                    filter: {
                        field: NetworkColumns.id,
                        operator: FilterOperators.Equals,
                        value: network.parent_network_id,
                    },
                    sort: {
                        direction: SortDirections.asc,
                        field: NetworkColumns.name,
                    },
                    pagination: {
                        page: 1,
                        perPage: 1,
                    },
                },
            });
            if (parentNetworkRes.results.length > 0) {
                parentNetwork = parentNetworkRes.results[0];
            }
        } catch (error) {
            console.error("Error fetching parent network:", error);
            // Continue without parent network
        }
    }

    // Get filter for league table
    // If there are no child networks, use the network ID
    // Otherwise, use the child network IDs
    const leagueTableFilter: Filter = hasChildNetworks
        ? {
              operator: FilterOperators.Or,
              filters: [
                  ...childNetworks.results.map((childNetwork) => ({
                      operator: FilterOperators.Equals,
                      field: TokenTimeseriesColumns.networkID,
                      value: childNetwork.id,
                  })),
              ],
          }
        : {
              operator: FilterOperators.Equals,
              field: TokenTimeseriesColumns.networkID,
              value: network.id,
          };

    const [
        // League Tables
        leagueTableDistributed,
        leagueTableRepresented,
        leagueTableDistributedAndRepresented,
    ] = await Promise.all([
        // League Table - Distributed
        queryTokenAggregateLeagueTable({
            name: `network-individual--${network.slug}--distributed`,
            query: {
                filter: {
                    operator: FilterOperators.And,
                    filters: [
                        leagueTableFilter,
                        {
                            operator: FilterOperators.Equals,
                            field: TokenTimeseriesColumns.tokenizationTypeID,
                            value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                        },
                        // {
                        //     operator: FilterOperators.NotEquals,
                        //     field: TokenTimeseriesColumns.assetClassId,
                        //     value: ASSET_CLASS_IDS.Stablecoin,
                        // },
                        {
                            operator: FilterOperators.Or,
                            filters: [
                                {
                                    operator: FilterOperators.And,
                                    filters: [
                                        {
                                            operator: FilterOperators.Equals,
                                            field: TokenTimeseriesColumns.assetClassID,
                                            value: ASSET_CLASS_IDS.TreasuryProduct,
                                        },
                                        {
                                            operator: FilterOperators.Equals,
                                            field: TokenTimeseriesColumns.isInvestable,
                                            value: true,
                                        },
                                    ],
                                },
                                {
                                    operator: FilterOperators.NotEquals,
                                    field: TokenTimeseriesColumns.assetClassID,
                                    value: ASSET_CLASS_IDS.TreasuryProduct,
                                },
                            ],
                        },
                    ],
                },
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.assetClasses,
                LeagueTableGroupTypes.managers,
            ],
        }),

        // League Table - Represented
        queryTokenAggregateLeagueTable({
            name: `network-individual--${network.slug}--represented`,
            query: {
                filter: {
                    operator: FilterOperators.And,
                    filters: [
                        leagueTableFilter,
                        {
                            operator: FilterOperators.Equals,
                            field: TokenTimeseriesColumns.tokenizationTypeID,
                            value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                        },
                        // {
                        //     operator: FilterOperators.NotEquals,
                        //     field: TokenTimeseriesColumns.assetClassId,
                        //     value: ASSET_CLASS_IDS.Stablecoin,
                        // },
                        {
                            operator: FilterOperators.Or,
                            filters: [
                                {
                                    operator: FilterOperators.And,
                                    filters: [
                                        {
                                            operator: FilterOperators.Equals,
                                            field: TokenTimeseriesColumns.assetClassID,
                                            value: ASSET_CLASS_IDS.TreasuryProduct,
                                        },
                                        {
                                            operator: FilterOperators.Equals,
                                            field: TokenTimeseriesColumns.isInvestable,
                                            value: true,
                                        },
                                    ],
                                },
                                {
                                    operator: FilterOperators.NotEquals,
                                    field: TokenTimeseriesColumns.assetClassID,
                                    value: ASSET_CLASS_IDS.TreasuryProduct,
                                },
                            ],
                        },
                    ],
                },
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.assetClasses,
                LeagueTableGroupTypes.managers,
            ],
        }),

        // League Table - Distributed + Represented
        queryTokenAggregateLeagueTable({
            name: `network-individual--${network.slug}--distributed-and-represented`,
            query: {
                filter: {
                    operator: FilterOperators.And,
                    filters: [
                        leagueTableFilter,
                        // {
                        //     operator: FilterOperators.NotEquals,
                        //     field: TokenColumns.assetClassID,
                        //     value: ASSET_CLASS_IDS.Stablecoin,
                        // },
                        {
                            operator: FilterOperators.Or,
                            filters: [
                                {
                                    operator: FilterOperators.And,
                                    filters: [
                                        {
                                            operator: FilterOperators.Equals,
                                            field: TokenTimeseriesColumns.assetClassID,
                                            value: ASSET_CLASS_IDS.TreasuryProduct,
                                        },
                                        {
                                            operator: FilterOperators.Equals,
                                            field: TokenTimeseriesColumns.isInvestable,
                                            value: true,
                                        },
                                    ],
                                },
                                {
                                    operator: FilterOperators.NotEquals,
                                    field: TokenTimeseriesColumns.assetClassID,
                                    value: ASSET_CLASS_IDS.TreasuryProduct,
                                },
                            ],
                        },
                    ],
                },
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.assetClasses,
                LeagueTableGroupTypes.managers,
            ],
        }),
    ]);

    // Query Network Aggregates
    const networkIDs = [network.id, ...childNetworks.results.map((n) => n.id)];
    const rwaAssetCount = [network, ...childNetworks.results].reduce(
        (total, n) => total + getNetworkRwaAssetCount(n),
        0,
    );

    const networkFilters: Filter[] = networkIDs.map((networkID) => ({
        field: TokenTimeseriesColumns.networkID,
        operator: FilterOperators.Equals,
        value: networkID,
    }));

    const networkFilter: Filter =
        networkFilters.length === 1
            ? networkFilters[0]
            : {
                  operator: FilterOperators.Or,
                  filters: networkFilters,
              };

    const excludeStablecoinFilter: Filter = {
        field: TokenTimeseriesColumns.assetClassID,
        operator: FilterOperators.NotEquals,
        value: ASSET_CLASS_IDS.Stablecoin,
    };

    const stablecoinOnlyFilter: Filter = {
        field: TokenTimeseriesColumns.assetClassID,
        operator: FilterOperators.Equals,
        value: ASSET_CLASS_IDS.Stablecoin,
    };

    const [
        distributedValue,
        representedValue,
        rwaHolders,
        rwaTransferVolume,
        stablecoinMarketCap,
        stablecoinHolders,
        stablecoinTransferVolume,
    ] = await Promise.all([
        queryTokenAggregateValueWith30D({
            name: `network-individual--${network.slug}--distributed-asset-value`,
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                networkFilter,
                excludeStablecoinFilter,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
            ],
        }),
        queryTokenAggregateValueWith30D({
            name: `network-individual--${network.slug}--represented-asset-value`,
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                networkFilter,
                excludeStablecoinFilter,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
            ],
        }),
        queryTokenAggregateValueWith30D({
            name: `network-individual--${network.slug}--total-rwa-holders`,
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: [
                networkFilter,
                excludeStablecoinFilter,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
            ],
        }),
        queryTokenAggregateValueWith30D({
            name: `network-individual--${network.slug}--total-rwa-transfer-volume`,
            measureID: MEASURE_IDS.Trailing30DayTransferVolumeDollar,
            filters: [networkFilter, excludeStablecoinFilter],
        }),
        queryTokenAggregateValueWith30D({
            name: `network-individual--${network.slug}--total-stablecoin-value`,
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [networkFilter, stablecoinOnlyFilter],
        }),
        queryTokenAggregateValueWith30D({
            name: `network-individual--${network.slug}--total-stablecoin-holders`,
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: [networkFilter, stablecoinOnlyFilter],
        }),
        queryTokenAggregateValueWith30D({
            name: `network-individual--${network.slug}--stablecoin-transfer-volume`,
            // measureID: MEASURE_IDS.Trailing30DayTransferVolumeDollar,
            // filters: [networkFilter, stablecoinOnlyFilter],
            measureID: MEASURE_IDS.DailyTransferVolumeDollar,
            filters: [
                networkFilter,
                stablecoinOnlyFilter,
                {
                    field: TokenTimeseriesColumns.date,
                    operator: FilterOperators.OnOrAfter,
                    value: THIRTY_DAYS_AGO.toISOString(),
                },
            ],
            filters30D: [
                networkFilter,
                stablecoinOnlyFilter,
                {
                    field: TokenTimeseriesColumns.date,
                    operator: FilterOperators.OnOrAfter,
                    value: SIXTY_DAYS_AGO.toISOString(),
                },
                {
                    field: TokenTimeseriesColumns.date,
                    operator: FilterOperators.OnOrBefore,
                    value: THIRTY_DAYS_AGO.toISOString(),
                },
            ],
            mode: AggregateQueryMode.flow,
        }),
    ]);

    const networkAggregates: Array<AggregateDisplayProps> = [
        {
            label: "Distributed Asset Value",
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
            value: distributedValue.value,
            type: AggregateValueTypes.dollarCompact,
            percentChange: {
                value: distributedValue.change30D,
                interval: "30d",
            },
        },
        {
            label: "Represented Asset Value",
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
            value: representedValue.value,
            type: AggregateValueTypes.dollarCompact,
            percentChange: {
                value: representedValue.change30D,
                interval: "30d",
            },
        },
        {
            label: "RWA Holders",
            value: rwaHolders.value,
            type: AggregateValueTypes.count,
            percentChange: {
                value: rwaHolders.change30D,
                interval: "30d",
            },
        },
        {
            label: "RWA 30D Transfer Volume",
            value: rwaTransferVolume.value,
            type: AggregateValueTypes.dollarCompact,
            percentChange: {
                value: rwaTransferVolume.change30D,
                interval: "30d",
            },
        },
        {
            label: "RWA Count",
            value: rwaAssetCount,
            type: AggregateValueTypes.count,
        },
        {
            label: "Stablecoin Market Cap",
            value: stablecoinMarketCap.value,
            type: AggregateValueTypes.dollarCompact,
            percentChange: {
                value: stablecoinMarketCap.change30D,
                interval: "30d",
            },
        },
        {
            label: "Stablecoin Holders",
            value: stablecoinHolders.value,
            type: AggregateValueTypes.countCompact,
            percentChange: {
                value: stablecoinHolders.change30D,
                interval: "30d",
            },
        },
        {
            label: "Stablecoin 30D Transfer Volume",
            value: stablecoinTransferVolume.value,
            type: AggregateValueTypes.dollarCompact,
            percentChange: {
                value: stablecoinTransferVolume.change30D,
                interval: "30d",
            },
        },
    ];

    // // // //

    return {
        props: {
            network,
            tokenFilterMetadata,
            serviceProviders,
            childNetworks: childNetworks.results,
            aggregates: networkAggregates,
            parentNetwork: parentNetwork
                ? {
                      id: parentNetwork.id,
                      name: parentNetwork.name,
                      slug: parentNetwork.slug,
                      icon_url: parentNetwork.icon_url,
                      color_hex: parentNetwork.color_hex,
                  }
                : null,
            leagueTableTabs: {
                all: leagueTableDistributedAndRepresented,
                distributed: leagueTableDistributed,
                represented: leagueTableRepresented,
            },
        },
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
};

// // // //

interface Props {
    network: Network;
    childNetworks: Network[];
    aggregates: Array<AggregateDisplayProps>;
    parentNetwork: ParentNetworkMetadata | null;
    tokenFilterMetadata: FilterMetadata[];
    leagueTableTabs: LeagueTableTabsByDistributionType;
    serviceProviders: QueryResponse<ServiceProvider>;
}

export default function NetworkPage(props: Props) {
    const {
        network,
        aggregates,
        tokenFilterMetadata,
        serviceProviders,
        childNetworks,
        parentNetwork,
        leagueTableTabs,
    } = props;

    const pageUrl = addBaseUrl(getNetworkURL({ networkSlug: network.slug }));
    const description = getNetworkPageDescription({ network });

    return (
        <PageTrack route={Routes.networkIndividual}>
            <NextSeo
                title={network.name}
                description={description}
                canonical={pageUrl}
                openGraph={{
                    type: "website",
                    url: pageUrl,
                    title: network.name,
                    site_name: "RWA.xyz",
                    description,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content: [
                            network.name,
                            "blockchain network",
                            "RWA",
                            "tokenized assets",
                            `tokenized assets on ${network.name}`,
                            "real world assets",
                        ].join(", "),
                    },
                ]}
            />
            <NetworkIndividualWebpageSEO network={network} />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Networks",
                        href: Routes.networks,
                    },
                    {
                        name: network.name,
                        href: getNetworkURL({ networkSlug: network.slug }),
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => (
                    <Navbar route={Routes.networkIndividual} user={user} />
                )}
            </UserFetcher>
            <SidebarLayout pathname={Routes.networkIndividual}>
                <Breadcrumbs
                    pages={[
                        {
                            name: "Networks",
                            href: Routes.networks,
                        },
                        {
                            name: network.name,
                            current: true,
                            icon_url: network.icon_url,
                        },
                    ]}
                />
                <PageWrapper>
                    <NetworkIndividualPage
                        network={network}
                        aggregates={aggregates}
                        childNetworks={childNetworks}
                        parentNetwork={parentNetwork}
                        tokenFilterMetadata={tokenFilterMetadata}
                        serviceProviders={serviceProviders}
                        leagueTableTabs={leagueTableTabs}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
